import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Phone, Clock, Star, Trophy, Sparks, Gift, CheckCircle, Shield } from "iconoir-react"
import Loading from "./Loading"

const REFERRAL_UUID_KEY = "vibrato_referral_uuid"

const Referral = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [, setReferralStored] = useState(false)

  useEffect(() => {
    const uuid = searchParams.get("uuid")

    if (uuid) {
      // Store the referral UUID in localStorage
      localStorage.setItem(REFERRAL_UUID_KEY, uuid)

      // Optional: Set an expiration timestamp (e.g., 30 days from now)
      const expirationTime = Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
      localStorage.setItem(`${REFERRAL_UUID_KEY}_expires`, expirationTime.toString())

      console.log("Referral UUID stored:", uuid)
      setReferralStored(true)
    }

    // Show the landing page instead of redirecting immediately
    setIsLoading(false)
  }, [searchParams])

  const handleGetStarted = () => {
    navigate("/signup", { replace: true })
  }

  const handleLogin = () => {
    navigate("/dashboard", { replace: true })
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 flex flex-col">
      {/* Simple Header */}
      <div className="w-full px-4 sm:px-6 py-4 sm:py-6 bg-white/80 backdrop-blur-sm border-b border-purple-100">
        <div className="max-w-6xl mx-auto flex justify-center">
          <a
            href="https://getvibrato.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
          >
            <img src="/vibrato-logo.svg" alt="Vibrato" className="h-8 sm:h-8" />
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* Success Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-3 bg-green-100 text-green-800 rounded-full font-medium mb-6 sm:mb-8 text-base">
            <CheckCircle className="w-5 h-5" />
            You've been referred to Vibrato!
          </div>

          {/* Hero Section */}
          <div className="mb-4 flex flex-col items-center justify-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Welcome to{" "}
              <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Vibrato
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
              Your friend thinks you'll love our AI-powered phone calling service. Join thousands who are already saving
              time and money on calls.
            </p>

            {/* Special Offer - Redesigned */}
            <div className="max-w-sm w-full bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 mx-2 shadow-md">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Gift className="w-6 h-6 text-green-600 flex-shrink-0" />
                <span className="font-bold text-lg sm:text-xl text-green-800">Referral Bonus</span>
              </div>
              <p className="text-green-700 font-semibold text-base sm:text-lg">Get 90 free minutes total!</p>
              <p className="text-green-600 text-sm sm:text-base mt-1">30 free minutes + 60 bonus minutes</p>
            </div>
          </div>

          {/* Call to Action - Mobile Optimized */}
          <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-12">
            <button
              onClick={handleGetStarted}
              className="w-full max-w-sm sm:max-w-md mx-auto py-4 px-6 sm:px-8 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 hover:scale-105 cursor-pointer"
            >
              <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="hidden sm:inline">Get Started Now - Claim 90 Free Minutes</span>
              <span className="sm:hidden">Get Started - 90 Free Minutes</span>
            </button>

            <button
              onClick={handleLogin}
              className="w-full max-w-sm sm:max-w-md mx-auto py-3 px-4 sm:px-6 bg-white hover:bg-gray-50 text-purple-600 font-medium text-base rounded-xl border border-purple-200 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105 hover:border-purple-300"
            >
              <span className="hidden sm:inline">Already have an account? Log In</span>
              <span className="sm:hidden">Log In</span>
            </button>

            <p className="text-gray-500 text-sm sm:text-base px-4">Free to start • No credit card required</p>
          </div>

          {/* Value Propositions - Mobile Optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 px-2">
            <div className="p-5 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 flex items-center justify-center mb-4 mx-auto">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Skip Hold Times</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Never wait on hold again. Our AI handles the waiting for you.
              </p>
            </div>

            <div className="p-5 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mb-4 mx-auto">
                <Star className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Lower Your Bills</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Automatically negotiate better rates on utilities, insurance, and more.
              </p>
            </div>

            <div className="p-5 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-amber-100">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center mb-4 mx-auto">
                <Trophy className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-base sm:text-lg">Stay in Control</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Monitor calls live with real-time transcripts and audio.
              </p>
            </div>

            <div className="p-5 sm:p-6 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-white sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-4 mx-auto">
                <Sparks className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold mb-2 text-base sm:text-lg">Join 10,000+ Users</h3>
              <p className="text-purple-100 text-sm sm:text-base">People saving hours every month on phone calls.</p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-gray-500 mt-12">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span>SOC 2 Protected</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-500" />
              <span>Trusted by 10,000+ users</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full px-4 sm:px-6 py-6 sm:py-8 bg-white/80 backdrop-blur-sm border-t border-purple-100">
        <div className="max-w-6xl mx-auto text-center">
          {/* CTA */}
          <div className="flex justify-center mb-6">
            <a
              href="https://getvibrato.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-sm rounded-lg hover:shadow-lg transition-all duration-200 hover:scale-105"
            >
              Learn More About Vibrato
            </a>
          </div>

          <div className="text-gray-500 text-sm sm:text-base">
            <p>© 2025 Vibrato Labs, Inc.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Utility function to retrieve the stored referral UUID
export const getReferralUuid = (): string | null => {
  const uuid = localStorage.getItem(REFERRAL_UUID_KEY)
  const expiration = localStorage.getItem(`${REFERRAL_UUID_KEY}_expires`)

  if (!uuid) return null

  // Check if the referral UUID has expired
  if (expiration && Date.now() > parseInt(expiration)) {
    localStorage.removeItem(REFERRAL_UUID_KEY)
    localStorage.removeItem(`${REFERRAL_UUID_KEY}_expires`)
    return null
  }

  return uuid
}

// Utility function to clear the stored referral UUID (call this after successful signup)
export const clearReferralUuid = (): void => {
  localStorage.removeItem(REFERRAL_UUID_KEY)
  localStorage.removeItem(`${REFERRAL_UUID_KEY}_expires`)
}

export default Referral
